/**
 * BatchGeoUserLocation taps into the browsers geolocation api to display the
 * users location on the map.
 *
 * @return {object} It is immediately invoked and only returns one public method,
 * `init` which has more in depth comments below.
 */
(function (window) {
  var BatchGeoUserLocation = function (options) {
    var userLocationMarker,
      userLocationWatcher,
      userLocationOverlay,
      centerCache,
      zoomCache,
      dragstartListener,
      $body,
      theMap = window.batchGeoGoogleMapsManager.getMap(),
      isMobile = BatchGeoDevices.is("mobile"),
      $recenterControlWrapper = $("<div />"),
      $geolocationControl = $("<button />"),
      $nearbyLocationControl = $("<button />"),
      locationUpdateThrottleTime = 2000, // in milliseconds
      initialMobileZoomHandled = false,
      displaySuccessAlert = false,
      noSleep = new window.NoSleep(),
      hasError = false,
      userLocationPersist = new BatchGeoPersistence({ key: "UserLocation" }),
      strings = BatchGeoStrings.getStringsForComponent("BatchGeoUserLocation");

    /**
     * geolocationSuccess updates the current user location in our redux store
     *
     * @param  {object} position data returned from the navigator.geolocation call
     * @return {undefined} returns nothing.
     */
    var geolocationSuccess = _.throttle(function (position) {
      BatchGeoStore.dispatch({
        type: "USER_LOCATION_LOCATION_UPDATE",
        latitude: parseFloat(position.coords.latitude.toPrecision(7)),
        longitude: parseFloat(position.coords.longitude.toPrecision(7)),
        accuracy: position.coords.accuracy,
      });
    }, locationUpdateThrottleTime);

    /**
     * geolocationError recieves the error data and passes it through to
     * the customAlert global function. It is called when the geolocation watcher
     * fails, or if `navigator.geolocation` is unavailable.
     *
     * @param  data        {object} error data to be displayed
     * @param  autoEnable  {boolean} whether or not the feature was enabled on page load
     * @return {undefined} returns nothing.
     */
    var geolocationError = function (data, autoEnable) {
      BatchGeoStore.dispatch({
        type: "USER_LOCATION_ACTIVE_DISABLE",
      });
      data = data || {};
      // if there is an error message passed from the geolocation api then clean
      // it up and use that, other wise use a default message.
      var errorMessage;
      switch (data.code) {
        case 1: // PERMISSION_DENIED
          errorMessage =
            "You've requested us to not access your location. If you've changed your mind please enable geolocation in your browser and try again.";
          break;
        case 2: // POSITION_UNAVAILABLE
          errorMessage =
            "Browser geolocation could not find your position,<br />please try again.";
          break;
        case 3: // TIMEOUT
          errorMessage =
            "Browser geolocation timeout.<br />Please check your connection and try again.";
          break;
        default:
          errorMessage = "Browser geolocation not available.";
      }

      // if geolocation was not auto enabled then we pop up the error message

      // The native code api passes an error with code 0 on gps disable, this will display
      // an error on iOS when disabling GPS for the user. So we don't want to display that alert
      // when we are on iOS AND the data.code is 0, because of boolean logic this switches
      // to display alert when NOT iOS OR data.code is NOT 0
      if (
        errorMessage &&
        !autoEnable &&
        !BatchGeo.iframeDetect() &&
        (!BatchGeoDevices.is("ios") || data.code !== 0)
      ) {
        // Don't show the errors in the future if the error is a user denied and they've seen
        // the error already.
        if (
          data.code !== 1 ||
          (data.code === 1 &&
            !userLocationPersist.fetch("permissionDeniedSeen"))
        ) {
          BatchGeo.customAlert(errorMessage);

          // If the error code was 1 we know now the custom alert was shown to the user and
          // we now can set this to be seen
          if (data.code === 1) {
            userLocationPersist.set("permissionDeniedSeen", true);
          }
        }
      }

      // send an analytics error event
      window.BatchGeoTrack.event({
        featureName: "geolocation",
        eventName: "error code " + data.code,
        eventValue: errorMessage,
      });

      $body.removeClass("waiting");

      // Record the error state so that if the user tries to enable, it doesn't just cycle
      hasError = true;
    };

    /**
     * enableGeolocation initializes the geolocation api position watcher.
     * It also adds a class of 'active' to the user location toggle button.
     *
     * Only necessary option currently is `autoEnable` (Boolean), which if true
     * tells it to use a stubbed out error function. This is so if an error happens
     * when geolocation is auto enabled on mobile we don't show the error message
     * to the user, since they didn't ask to turn it on.
     *
     * @param options {object} options hash, discussed above.
     * @return {undefined} returns nothing.
     */
    var enableGeolocation = function (options) {
      BatchGeoStore.dispatch({
        type: "USER_LOCATION_ACTIVE_ENABLE",
      });
      options = options || {};
      // cache the body on enable
      $body = $("body");

      // Check for window.webkit in case we are emulating ios on chrome
      if (BatchGeoDevices.is("iosapp") && window.webkit) {
        window.webkit.messageHandlers.nativeApp.postMessage({
          trackLocation: true,
          autoEnable: options.autoEnable,
        });
      } else {
        // initialize geolocation watcher
        userLocationWatcher = navigator.geolocation[
          isMobile ? "watchPosition" : "getCurrentPosition"
        ](
          geolocationSuccess,
          function (data) {
            // pass the autoEnable prop through to the error callback
            geolocationError(data, options.autoEnable);
          },
          // browser geolocation options
          {
            maximumAge: 0,
            enableHighAccuracy: isMobile,
          },
        );
      }

      // set cursor to waiting so user knows something is happening, otherwise
      // it's unclear if anything worked.
      $body.addClass("waiting");
    };

    /**
     * NoSleep has a bug that if you call disable when it's not enabled
     * (such as if you call disable twice) it throws an error. This function
     * is a helper to check that noSleep has an instance to disable. This is
     * a crappy patch that uses an internal private property to check if we
     * can disable the wake lock but it's the recommended work around.
     *
     * @see {@link https://github.com/richtr/NoSleep.js/issues/105}
     */
    var disableNoSleep = function () {
      if (!BatchGeoDevices.is("iosapp") && noSleep._wakeLock) {
        noSleep.disable();
      }
    };

    /**
     * For geolocation we only want to show tooltips on mobile
     */
    var showGeolocationTooltip = function (content) {
      if (isMobile) {
        BatchGeoStore.dispatch({
          type: "SHOW_TOOLBAR_TOOLTIP",
          tooltipContent: content,
        });
      }
    };

    /**
     * Updates the geolocation button's active state
     * @param {boolean} active
     */
    var activeHandler = function (active) {
      // toggle doesn't work for mobile + desktop since mobile cycles different states
      if (active && !$geolocationControl.hasClass("active")) {
        $geolocationControl.addClass("active");
      } else if (!active) {
        $geolocationControl.removeClass("active");
      }
    };

    /**
     * getLocationState simple fetches the UserLocation portion of the redux state
     *
     * @returns {object}
     */
    var getLocationState = function () {
      return BatchGeoStore.getState().UserLocation;
    };

    //call this function to switch the location icon to the following icon and vice versa
    var toggleIcon = function () {
      var $geolocationButton = $geolocationControl.find(
        ".geolocation-control-icon",
      );
      var userLocationState = getLocationState();
      var isFollowMode = userLocationState.cycle === 1;

      //following icon should only be on if the toolbar is persisting (if map is in 'following' mode)
      $geolocationButton.toggleClass("toolbar-icon-location", !isFollowMode);
      $geolocationButton.toggleClass("toolbar-icon-following", isFollowMode);
    };

    /**
     * disableGeolocation disables the user geolocation feature. To do this,
     * first the user location marker is removed, and then the geolocation
     * position watcher is cleared.
     *
     * @return {undefined} returns nothing.
     */
    var disableGeolocation = function () {
      BatchGeoStore.dispatch({
        type: "USER_LOCATION_ACTIVE_DISABLE",
      });
      // remove the marker overlay
      if (userLocationOverlay) {
        userLocationOverlay.setMap(null);
        userLocationOverlay = null;
      }

      if (BatchGeoDevices.is("ios") && window.webkit) {
        // send location off message to iOS app
        window.webkit.messageHandlers.nativeApp.postMessage({
          trackLocation: false,
        });
        window.webkit.messageHandlers.nativeApp.postMessage({
          wakeLock: false, // tell iOS app to allow screen to idle again
        });
      } else {
        // clear position watcher
        navigator.geolocation.clearWatch(userLocationWatcher);
        disableNoSleep();
      }

      showGeolocationTooltip(strings.get("GEOLOCATION_DISABLED"));
      activeHandler(false);
      toggleIcon();
      displaySuccessAlert = false;
    };
    /**
     * toggleHandler is called to toggle the state of the users HTML5
     * geolocation position.
     *
     * @return {undefined} returns nothing.
     */
    var toggleHandler = function (state) {
      // First check if browser has geolocation
      var geolocationDisable =
        BatchGeo.getQueryVariable("geolocation") == "false";
      if (navigator.geolocation) {
        if (state.active && !geolocationDisable) {
          // if feature state is now active, enable it
          enableGeolocation();
        } else {
          disableGeolocation();
        }
      } else {
        geolocationError();
      }
    };

    var getLatLngFromState = function (state) {
      return new google.maps.LatLng(state.latitude, state.longitude);
    };

    /**
     * dragstartHandler just caches whether mobile has been panned or not.
     *
     * @return {undefined} returns nothing.
     */
    var dragstartHandler = function () {
      BatchGeoStore.dispatch({
        type: "USER_LOCATION_PANNED",
        hasPanned: true,
      });
    };

    /**
     * cycleHandler cycles through a few different states for mobile
     *
     * @return {undefined} returns nothing.
     */
    var cycleHandler = function (state) {
      // If there was an error, don't cycle, just attempt to enableGeolocation, for some reason
      // there is a state bug that crashes clarker if we try to go back to cycle 0 on error
      if (hasError) {
        enableGeolocation({ autoEnable: false });
        return;
      }

      /** default at mobile page load : geolocation watch is on with no zoom */
      switch (state.cycle) {
        case 1:
          /** 0: follow position */
          // caching values for later use
          zoomCache = theMap.getZoom();
          centerCache = theMap.getCenter();
          // setting center of map to whatever the current gps latlng is
          theMap.panTo(getLatLngFromState(state));
          // zooming to a reasonable level for following a gps marker
          if (theMap.getZoom() < 15) {
            theMap.setZoom(15);
          }
          showGeolocationTooltip(strings.get("FOLLOWING_LOCATION"));

          if (BatchGeoDevices.is("ios") && window.webkit) {
            // send location off message to iOS app
            window.webkit.messageHandlers.nativeApp.postMessage({
              wakeLock: true, // tell iOS app to lock screen on
            });
          } else {
            // Use no sleep for non iOS devices
            noSleep.enable();
          }

          activeHandler(state.active);
          toggleIcon();

          // start listening for the dragstart event
          dragstartListener = theMap.addListener("dragstart", dragstartHandler);
          break;
        case 2:
          /** 1 : zoom back to cached zoom and hide geolocation marker */
          // remove dragstart listeners
          google.maps.event.removeListener(dragstartListener);

          theMap.setCenter(centerCache);
          theMap.setZoom(zoomCache);
          disableGeolocation();

          break;
        default:
          /** 2 : reset to default, geolocation is on with no movement/zoom */
          enableGeolocation();
      }
    };

    var userDistance = function (p1, p2) {
      // Thanks Pythagoras... a^2 + b^2 = c^2
      var a = p1.lat() - p2.lat();
      var b = p1.lng() - p2.lng();
      return Math.sqrt(a * a + b * b);
    };
    /**
     * zoomToNearestMarker zooms out to show the users geolocation marker and the closest map marker
     *
     * @return {undefined} returns nothing.
     */
    var zoomToNearestMarker = function () {
      var state = getLocationState();

      // If GPS is not active, first cycle to enabled
      if (!state.active) {
        BatchGeoStore.dispatch({
          type: "USER_LOCATION_ACTIVE_CYCLE",
          cycle: 0,
        });
      }
      // If we are following when zoom is pressed, reset map and change to just geolocation
      if (state.cycle === 1) {
        BatchGeoStore.dispatch({
          type: "USER_LOCATION_ACTIVE_CYCLE",
          cycle: 0,
        });

        if (BatchGeoDevices.is("ios") && window.webkit) {
          window.webkit.messageHandlers.nativeApp.postMessage({
            wakeLock: false, // tell iOS app to allow screen to idle again
          });
        } else {
          disableNoSleep();
        }
        google.maps.event.removeListener(dragstartListener);
        toggleIcon();
      }

      showGeolocationTooltip(strings.get("ZOOM_TO_NEAREST_MARKER"));

      // If we have a valid user location marker, set the bounds to the closest marker
      if (userLocationMarker) {
        var userPosition = userLocationMarker.getPosition();

        var markers = window.batchGeoGoogleMapsMarker.getMarkers();
        var markerPositions = markers.map(function (marker) {
          var coords = marker.position;
          coords.distance = userDistance(coords, userPosition);
          return coords;
        });

        var closestPointToMe = markerPositions.reduce(function (acc, val) {
          return acc.distance > val.distance ? val : acc;
        }, markerPositions[0]);

        var newBounds = new google.maps.LatLngBounds();
        newBounds.extend(userPosition);
        newBounds.extend(closestPointToMe);
        theMap.fitBounds(newBounds);
      }
    };

    var customLocationDraw = function () {
      if (!this.marker || !this.projection) {
        return;
      }

      var icon = this.marker.activeIcon || this.marker.icon;
      var iconWidth = 24,
        iconHeight = 24;
      var anchorOffset = 12;

      var location = this.projection.fromLatLngToDivPixel(this.marker.position);

      // If we don't already have a point imitator, create one
      if (!this.$point) {
        var fragment = document.createDocumentFragment();
        this.$point = $(
          '<div class="map-point" style="' +
            "width:32px; height:32px; " +
            "left:" +
            location.x +
            "px; top:" +
            location.y +
            "px; " +
            "position:absolute; cursor:grab;" +
            '">' +
            '<div style="' +
            "top:-" +
            anchorOffset +
            "px;" +
            "left:-" +
            anchorOffset +
            "px;" +
            "height:" +
            iconHeight +
            "px;" +
            "width:" +
            iconWidth +
            "px;" +
            "position: relative;" +
            'overflow: hidden">' +
            '<img src="' +
            icon.url.replace(/"/g, "'") +
            '" width="100%">' +
            "</div>" +
            "</div>",
        );

        fragment.appendChild(this.$point.get(0));
        this.markerLayer.append(fragment);
      } else {
        // otherwise just update it's position
        this.$point.css({
          left: location.x,
          top: location.y,
        });
      }
    };

    /**
     * createOrUpdateGPSMarker creates the GPS clarker overlay marker from the coordinate state, or updates the
     * location of the existing overlay marker.
     *
     * @param  {object} state - Redux state
     * @return {undefined} returns nothing.
     */
    var createOrUpdateGPSMarker = function (state) {
      var latlng = getLatLngFromState(state);

      if (!userLocationMarker) {
        // if no marker create a new marker
        // but leave out the map prop so it doesn't append it yet
        var iconWidth = 16;
        userLocationMarker = new google.maps.Marker({
          position: latlng,
          optimized: false,
          icon: BatchGeoSvgMarkerIcon({
            type: "gpsDot",
            template: {
              fill: "blue",
              width: iconWidth,
              height: iconWidth,
            },
          }),
          title: "Your location (within a " + state.accuracy + " meter radius)",
        });
      } else {
        // other wise just update the coordinates of the existing marker.
        userLocationMarker.setPosition(latlng);
      }
      // Create an overlay if we don't have one
      if (!userLocationOverlay) {
        userLocationOverlay = new window.ClarkerMarkerOverlay(
          theMap,
          userLocationMarker,
        );
        // Override the clarkoverlay draw to handle our user location updates
        userLocationOverlay.draw = customLocationDraw;
      } else {
        // otherwise, call the overlays draw method to recalculate the faux marker position
        userLocationOverlay.draw();
      }
    };

    /**
     * geolocationUpdate sets the map over the returned geolocation coords,
     * and creates a marker overlay at those coords.
     *
     * @param  {object} state - Redux state
     * @return {undefined} returns nothing.
     */
    var geolocationUpdate = function (state) {
      if (!state.latitude || !state.longitude || state.cycle === 2) return;

      if (!initialMobileZoomHandled) {
        initialMobileZoomHandled = true;
        window.batchGeoMap.zoomToClosestMarkerUsingNativeGPS();
      }

      // If we successfully updated our location for the first time
      // send a success message and set the location button to active (blue)
      // This bool gets reset when the user disabled GPS
      if (!displaySuccessAlert) {
        displaySuccessAlert = true;
        showGeolocationTooltip(strings.get("GEOLOCATION_ENABLED"));
        activeHandler(true);
      }

      createOrUpdateGPSMarker(state);

      // if initial cycle is active then we check for mobile and that the mobile
      // has not been panned yet. If so then the map follows the moving marker.
      if (state.cycle === 1 && isMobile && !state.hasPanned) {
        theMap.panTo(getLatLngFromState(state));
      }

      // Pan to marker when enabling in Desktop mode
      if (!isMobile) {
        theMap.panTo(getLatLngFromState(state));
      }

      // finally revert cursor styles back to standard.
      $body.removeClass("waiting");
      hasError = false;
    };

    /**
     * Takes a name a settings and returns a geolocation button with an icon
     *
     * @param buttonName
     * @param buttonOpts
     * @returns {*|jQuery|HTMLElement}
     */
    var createControl = function (buttonName, buttonOpts) {
      var buttonSettings = $.extend(
        {
          icon: "", // the icon to use for the button
          className: "", // will be put directly into class="HERE"
          action: function () {}, // what to fire when you click the button
        },
        buttonOpts,
      );

      var $icon = $("<span />", {
        class: "geolocation-control-icon toolbar-icon-" + buttonSettings.icon,
        "aria-hidden": true,
      });

      var $button = $("<button />", {
        class: "geolocation-control round " + buttonSettings.className,
        title: buttonName,
      });

      $button.append($icon);

      $button.on("click", buttonSettings.action);

      return $button;
    };

    /**
     * Recenter control is rectangular, has text, and has no icon.
     * Little diff than the default control which will be reused for future
     * functionality like "nearby markers"
     *
     * @returns {*|jQuery|HTMLElement}
     */
    var createRecenterControl = function () {
      var $button = $("<button />", {
        class: "geolocation-control recenter",
        title: "recenter",
      });

      $button.text("RE-CENTER").on("click", function () {
        theMap.panTo(getLatLngFromState(getLocationState()));
        BatchGeoStore.dispatch({
          type: "USER_LOCATION_PANNED",
          hasPanned: false,
        });
        $recenterControlWrapper.empty();
      });

      return $button;
    };

    /**
     * renderControls creates and appends control wrappers and the default
     * geolocation control button.
     */
    var renderControls = function () {
      var $controlWrapper = $("<div />", {
        class: "batchgeo-geolocation-controls right",
      });

      $recenterControlWrapper = $("<div />", {
        class: "batchgeo-geolocation-controls left",
      });

      // need to render both control wrappers on initial control render
      $("#mapDiv").append([$controlWrapper, $recenterControlWrapper]);

      $geolocationControl = createControl("User Location", {
        icon: "location",
        className: "location",
        action: function () {
          // these controls are only rendered on mobile, use "cycle".
          BatchGeoStore.dispatch({
            type: "USER_LOCATION_ACTIVE_CYCLE",
          });
        },
      });

      $nearbyLocationControl = createControl("Nearby Location", {
        icon: "marker",
        className: "nearby",
        action: function () {
          zoomToNearestMarker();
        },
      });

      $controlWrapper.append([$nearbyLocationControl, $geolocationControl]);
    };

    /**
     * renderRecenterControl appends the recenter control to it's wrapper
     */
    var renderRecenterControl = function () {
      $recenterControlWrapper.append(createRecenterControl());
    };

    /**
     * init adds the store listener so it can call the private method.
     *
     * @return {undefined}      returns nothing.
     */
    var init = function () {
      // add the listener for location
      BatchGeoStore.addListener(
        "UserLocation",
        function (newState, oldState) {
          return (
            !_.isEqual(newState.latitude, oldState.latitude) ||
            !_.isEqual(newState.longitude, oldState.longitude)
          );
        },
        geolocationUpdate,
      );

      // add the listener to toggle/cycle the user location
      BatchGeoStore.addListener(
        "UserLocation",
        function (newState, oldState) {
          var prop = isMobile ? "cycle" : "active";
          return !_.isEqual(newState[prop], oldState[prop]);
        },
        function (newState) {
          isMobile ? cycleHandler(newState) : toggleHandler(newState);
        },
      );

      // add the listener for re-centering controls
      BatchGeoStore.addListener(
        "UserLocation",
        function (newState, oldState) {
          return (
            !_.isEqual(newState.cycle, oldState.cycle) ||
            !_.isEqual(newState.hasPanned, oldState.hasPanned)
          );
        },
        function (newState) {
          var followMode = 1;
          // if we are on the "follow" mode and the user has panned, offer the re-center control
          if (newState.cycle === followMode && newState.hasPanned) {
            renderRecenterControl();
          }
          // else if cycle moved to anything but "follow" mode remove the re-center control
          else if (newState.cycle !== followMode) {
            $recenterControlWrapper.empty();
          }
        },
      );

      // We hide the controls if in an iframe and on mobile
      // We can explore enabling these
      if (isMobile && !BatchGeo.iframeDetect()) {
        renderControls();
        enableGeolocation({ autoEnable: true });
      }
    };

    this.geolocationSuccess = geolocationSuccess;
    this.geolocationError = geolocationError;
    this.disableGeolocation = disableGeolocation;

    init(options);
  };
  window.BatchGeoUserLocation = BatchGeoUserLocation;
})(window);
